// src/App.js
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import Layout from './components/layout/Layout';

// Pages — ใช้ lazy loading เพื่อแยก bundle (ลดขนาดโหลดครั้งแรก ~60%)
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/DashboardPage';

const SubjectsPage = lazy(() => import('./pages/admin/SubjectsPage'));
const ClassroomsPage = lazy(() => import('./pages/admin/ClassroomsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const StudentsPage = lazy(() => import('./pages/students/StudentsPage'));
const MyStudentsPage = lazy(() => import('./pages/students/MyStudentsPage'));
const StudentSummaryPage = lazy(() => import('./pages/students/StudentSummaryPage'));
const IndicatorsPage = lazy(() => import('./pages/teacher/IndicatorsPage'));
const TeachersPage = lazy(() => import('./pages/admin/TeachersPage'));
const ClassroomSettingsPage = lazy(() => import('./pages/admin/ClassroomSettingsPage'));
const SchoolSettingsPage = lazy(() => import('./pages/admin/SchoolSettingsPage'));
const ScorePage = lazy(() => import('./pages/scoring/ScorePage'));
const FinalScorePage = lazy(() => import('./pages/scoring/FinalScorePage'));
const QualityScorePage = lazy(() => import('./pages/scoring/QualityScorePage'));
const SubjectResultPage = lazy(() => import('./pages/scoring/SubjectResultPage'));
const ScoreClassroomPage = lazy(() => import('./pages/scoring/ScoreClassroomPage'));
const ScoreRoomPage = lazy(() => import('./pages/scoring/ScoreRoomPage'));
const ScoreRoomMPage = lazy(() => import('./pages/scoring/ScoreRoomMPage'));
const ClassroomReportPage = lazy(() => import('./pages/scoring/ClassroomReportPage'));
const QualityReportPage = lazy(() => import('./pages/scoring/QualityReportPage'));
const EvaluationReportPage = lazy(() => import('./pages/scoring/EvaluationReportPage'));
const ClubsPage = lazy(() => import('./pages/admin/ClubsPage'));
const ClubDetailPage = lazy(() => import('./pages/admin/ClubDetailPage'));
const AttendancePage = lazy(() => import('./pages/attendance/AttendancePage'));
const AttendanceSettingsPage = lazy(() => import('./pages/attendance/AttendanceSettingsPage'));
const AttendanceReportPage = lazy(() => import('./pages/attendance/AttendanceReportPage'));
const SchedulePage = lazy(() => import('./pages/attendance/SchedulePage'));

const LazyFallback = () => (
  <div className="loading-screen"><div className="spinner" /><span>กำลังโหลด...</span></div>
);

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h2>เกิดข้อผิดพลาด</h2>
          <p style={{ color: '#666', marginBottom: 16 }}>{this.state.error?.message}</p>
          <button className="btn btn-primary" onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}>
            🔄 โหลดใหม่
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function PrivateRoute({ children, roles }) {
  const { user, loading, isImpersonating, realAdmin } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /><span>กำลังโหลด...</span></div>;
  if (!user) return <Navigate to="/login" replace />;
  // ตอน impersonate ใช้สิทธิ์ของ realAdmin (แอดมิน) ในการตรวจ route access
  const roleToCheck = isImpersonating ? realAdmin.role : user.role;
  if (roles && !roles.includes(roleToCheck)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />

      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<DashboardPage />} />

        {/* Admin / Academic / Assessment */}
        <Route path="subjects" element={<PrivateRoute roles={['admin','academic','assessment','teacher']}><Suspense fallback={<LazyFallback />}><SubjectsPage /></Suspense></PrivateRoute>} />
        <Route path="classrooms" element={<PrivateRoute roles={['admin','academic','assessment','teacher']}><Suspense fallback={<LazyFallback />}>{user?.role === 'teacher' ? <Navigate to="/my-students" replace /> : <ClassroomsPage />}</Suspense></PrivateRoute>} />
        <Route path="users" element={<PrivateRoute roles={['admin']}><Suspense fallback={<LazyFallback />}><UsersPage /></Suspense></PrivateRoute>} />
        <Route path="teachers" element={<PrivateRoute roles={['admin','academic','assessment','teacher']}><Suspense fallback={<LazyFallback />}><TeachersPage /></Suspense></PrivateRoute>} />
        <Route path="classroom-settings" element={<PrivateRoute roles={['admin','academic','assessment']}><Suspense fallback={<LazyFallback />}><ClassroomSettingsPage /></Suspense></PrivateRoute>} />
        <Route path="school-settings" element={<PrivateRoute roles={['admin']}><Suspense fallback={<LazyFallback />}><SchoolSettingsPage /></Suspense></PrivateRoute>} />
        <Route path="clubs" element={<PrivateRoute roles={['admin','academic','assessment','teacher']}><Suspense fallback={<LazyFallback />}><ClubsPage /></Suspense></PrivateRoute>} />
        <Route path="clubs/:clubId" element={<PrivateRoute roles={['admin','academic','assessment','teacher']}><Suspense fallback={<LazyFallback />}><ClubDetailPage /></Suspense></PrivateRoute>} />

        {/* Teacher */}
        <Route path="indicators/:subjectId/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><IndicatorsPage /></Suspense></PrivateRoute>} />

        {/* Scoring */}
        <Route path="score-classrooms" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><ScoreClassroomPage /></Suspense></PrivateRoute>} />
        <Route path="score-classrooms/:gradeKey" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><ScoreClassroomPage /></Suspense></PrivateRoute>} />
        <Route path="score-room/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><ScoreRoomPage /></Suspense></PrivateRoute>} />
        <Route path="score-room-m/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><ScoreRoomMPage /></Suspense></PrivateRoute>} />
        <Route path="scores/:subjectId/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><ScorePage /></Suspense></PrivateRoute>} />
        <Route path="final-scores/:subjectId/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><FinalScorePage /></Suspense></PrivateRoute>} />
        <Route path="quality-scores/:subjectId/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><QualityScorePage /></Suspense></PrivateRoute>} />
        <Route path="subject-result/:subjectId/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><SubjectResultPage /></Suspense></PrivateRoute>} />
        <Route path="classroom-report/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><ClassroomReportPage /></Suspense></PrivateRoute>} />
        <Route path="quality-report/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><QualityReportPage /></Suspense></PrivateRoute>} />
        <Route path="evaluation-report" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><EvaluationReportPage /></Suspense></PrivateRoute>} />

        {/* Attendance (เช็คชื่อรายคาบ) */}
        <Route path="attendance" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><AttendancePage /></Suspense></PrivateRoute>} />
        <Route path="attendance-report" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><AttendanceReportPage /></Suspense></PrivateRoute>} />
        <Route path="attendance-settings" element={<PrivateRoute roles={['admin','academic']}><Suspense fallback={<LazyFallback />}><AttendanceSettingsPage /></Suspense></PrivateRoute>} />
        <Route path="schedules" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><SchedulePage /></Suspense></PrivateRoute>} />

        {/* Students */}
        <Route path="my-students" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><MyStudentsPage /></Suspense></PrivateRoute>} />
        <Route path="student-summary" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><StudentSummaryPage /></Suspense></PrivateRoute>} />
        <Route path="students/:classroomId" element={<PrivateRoute><Suspense fallback={<LazyFallback />}><StudentsPage /></Suspense></PrivateRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
